import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", subject: "", message: "" };

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.success"));
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: MapPin, label: t("contact.infoAtelierLabel"), value: t("contact.infoAtelierValue") },
    { icon: Phone, label: t("contact.infoPhoneLabel"), value: "+352 621 830 601" },
    { icon: Mail, label: t("contact.infoEmailLabel"), value: "lionel@medernach-design.lu" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-40 bg-[#0B1622]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">
              {t("contact.eyebrow")}
            </p>
            <h2 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
              {t("contact.title")} <span className="gold-gradient-text italic">{t("contact.titleAccent")}</span>
            </h2>
            <p className="mt-8 text-[#A0ABC0] font-light leading-relaxed max-w-md">
              {t("contact.subtitle")}
            </p>

            <div className="mt-12 space-y-8">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-5" data-testid={`contact-info-${it.label}`}>
                  <it.icon className="text-[#D4AF37] mt-1 shrink-0" size={22} />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#A0ABC0]">{it.label}</p>
                    <p className="text-white mt-1">{it.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            data-testid="contact-form"
            className="lg:col-span-7 space-y-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input
                required
                value={form.name}
                onChange={update("name")}
                placeholder={t("contact.name")}
                data-testid="contact-name-input"
                className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 px-0 outline-none transition-colors duration-300 placeholder:text-[#A0ABC0]/60"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder={t("contact.email")}
                data-testid="contact-email-input"
                className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 px-0 outline-none transition-colors duration-300 placeholder:text-[#A0ABC0]/60"
              />
            </div>
            <input
              value={form.subject}
              onChange={update("subject")}
              placeholder={t("contact.subject")}
              data-testid="contact-subject-input"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 px-0 outline-none transition-colors duration-300 placeholder:text-[#A0ABC0]/60"
            />
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              placeholder={t("contact.message")}
              data-testid="contact-message-input"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 px-0 outline-none transition-colors duration-300 placeholder:text-[#A0ABC0]/60 resize-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-sm disabled:opacity-60"
            >
              {loading ? t("contact.submitting") : t("contact.submit")}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
